import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, SlidersHorizontal } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { discountService } from '../../services/discountService';
import MantleGrid, { MantleGridButton } from '../../components/MantleGrid';
import Field from '../../components/Field';
import { cell, GRID } from '../../styles/gridStyles';
import { PERIODS, periodLabel } from '../../utils/financialPeriods';
import { DISCOUNT_GROUPS, isEnabled } from '../../config/discountTypes';

// =============================================================================
// Discounts & Trade Spend — the gross-to-net "give-away" per customer (or
// customer group), per month. Discount types are grouped into collapsible
// column groups (same UX as the Stocked Pricing landed-cost stages); each type
// is a % of gross or a flat amount. Values are entered for the primary
// (lowest selected) month and saved across every selected month.
//
// Which discount TYPES appear here is controlled by the Discount Configuration
// screen (md_discount_config); disabled types are filtered out entirely.
//
// Gross sales is derived server-side (sales volume × selling price for the
// month, resolving any customer-group price override), so give-away % and trade
// spend are meaningful once volumes and prices are captured.
// =============================================================================

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const basisTag = (basis) => (basis === 'amt' ? 'R' : '%');

const LEVELS = [
  { code: 'customer', label: 'By Customer' },
  { code: 'group', label: 'By Customer Group' },
];

const pillClass = (on) =>
  `px-2 py-1 text-xs rounded border transition-colors ${
    on
      ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200'
      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
  }`;

const Discounts = () => {
  const { selectedVersion, fyStartMonth } = useAppContext();
  const navigate = useNavigate();

  const [level, setLevel] = useState('customer');
  const isCustomer = level === 'customer';

  const [selectedPeriods, setSelectedPeriods] = useState([1]); // months to apply to
  const primaryPeriod = selectedPeriods.length ? Math.min(...selectedPeriods) : null;

  const [entities, setEntities] = useState([]);       // [{ entity_code, entity_name, group_name }]
  const [values, setValues] = useState({});           // { `${entity}:${code}`: number } for primary month
  const [grossMap, setGrossMap] = useState({});       // { entity_code: gross }
  const [dirty, setDirty] = useState({});
  const [configMap, setConfigMap] = useState({});     // { discount_code: is_enabled } (deviations only)
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Column groups start collapsed (compact); missing = collapsed.
  const [collapsed, setCollapsed] = useState({});
  const [hiddenGroups, setHiddenGroups] = useState({}); // session view toggle
  const isCollapsed = useCallback((code) => collapsed[code] !== false, [collapsed]);

  // ---- Effective catalogue (config-filtered) -------------------------------
  // Only the discount types enabled in Discount Configuration are shown; groups
  // left with no enabled types drop out entirely.
  const effectiveGroups = useMemo(() =>
    DISCOUNT_GROUPS
      .map((g) => ({ ...g, types: g.types.filter((t) => isEnabled(configMap, t.code)) }))
      .filter((g) => g.types.length > 0),
    [configMap]);
  const effectiveTypes = useMemo(() => effectiveGroups.flatMap((g) => g.types), [effectiveGroups]);

  // ---- Load config once ----------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const res = await discountService.getConfig();
        if (res.success) {
          const map = {};
          res.data.forEach((r) => { map[r.discount_code] = r.is_enabled; });
          setConfigMap(map);
        }
      } catch { /* fall back to catalogue defaults */ }
    })();
  }, []);

  // ---- Load grid data ------------------------------------------------------
  const loadData = useCallback(async () => {
    if (!selectedVersion || !primaryPeriod) { setEntities([]); setValues({}); setGrossMap({}); return; }
    setIsLoading(true);
    setError(null);
    try {
      const res = await discountService.getDiscounts(selectedVersion, { level, period: primaryPeriod });
      if (!res.success) throw new Error(res.message);
      setEntities(res.data.entities || []);
      const vmap = {};
      (res.data.values || []).forEach((v) => { vmap[`${v.entity_code}:${v.discount_code}`] = Number(v.value) || 0; });
      setValues(vmap);
      const gmap = {};
      (res.data.gross || []).forEach((g) => { gmap[g.entity_code] = Number(g.gross) || 0; });
      setGrossMap(gmap);
      setDirty({});
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load discounts.');
      setEntities([]); setValues({}); setGrossMap({});
    } finally {
      setIsLoading(false);
    }
  }, [selectedVersion, level, primaryPeriod]);

  useEffect(() => { loadData(); }, [loadData]);

  // ---- Calculations --------------------------------------------------------
  const valueOf = useCallback((entity, code) => Number(values[`${entity}:${code}`] || 0), [values]);
  const grossOf = useCallback((entity) => Number(grossMap[entity] || 0), [grossMap]);

  // Spend (R) a group contributes for an entity: gross × Σ% / 100 + Σ flat.
  const groupSpend = useCallback((entity, group) => {
    const gross = grossOf(entity);
    return group.types.reduce((a, t) => {
      const v = valueOf(entity, t.code);
      return a + (t.basis === 'amt' ? v : (gross * v) / 100);
    }, 0);
  }, [grossOf, valueOf]);

  const visibleGroups = useMemo(() => effectiveGroups.filter((g) => !hiddenGroups[g.code]), [effectiveGroups, hiddenGroups]);

  const entitySpend = useCallback((entity) =>
    visibleGroups.reduce((a, g) => a + groupSpend(entity, g), 0), [visibleGroups, groupSpend]);
  const entityGiveaway = useCallback((entity) => {
    const gross = grossOf(entity);
    return gross > 0 ? (entitySpend(entity) / gross) * 100 : 0;
  }, [grossOf, entitySpend]);

  // Which columns are on screen (collapsed group -> 1 spend column).
  const visibleCols = useMemo(() => {
    const cols = [];
    for (const g of effectiveGroups) {
      if (hiddenGroups[g.code]) continue;
      if (isCollapsed(g.code)) cols.push({ kind: 'group', group: g });
      else g.types.forEach((t, i) => cols.push({ kind: 'type', group: g, type: t, first: i === 0 }));
    }
    return cols;
  }, [effectiveGroups, hiddenGroups, isCollapsed]);

  // ---- Grid model ----------------------------------------------------------
  const columns = useMemo(() => ([
    { columnId: 'entity', width: 220, resizable: true },
    ...(isCustomer ? [{ columnId: 'group', width: 150, resizable: true }] : []),
    ...visibleCols.map((vc) =>
      vc.kind === 'group'
        ? { columnId: `G:${vc.group.code}`, width: 130, resizable: true }
        : { columnId: `T:${vc.type.code}`, width: 132, resizable: true }
    ),
    { columnId: 'GROSS', width: 130, resizable: true },
    { columnId: 'GIVEAWAY', width: 116, resizable: true },
    { columnId: 'SPEND', width: 140, resizable: true },
  ]), [visibleCols, isCustomer]);

  const gridRows = useMemo(() => {
    const headerCells = [
      cell.header(isCustomer ? 'Customer' : 'Customer Group'),
      ...(isCustomer ? [cell.header('Group')] : []),
      ...visibleCols.map((vc) => {
        if (vc.kind === 'group') return cell.drillHeader(`▸ ${vc.group.short}`, 'quarter');
        const label = `${vc.type.label} (${basisTag(vc.type.basis)})`;
        return vc.first ? cell.drillHeader(`▾ ${label}`, 'year') : cell.header(label, { align: 'right' });
      }),
      cell.header("Gross Sales", { align: 'right' }),
      cell.header('Give-away %', { align: 'right' }),
      cell.header('Trade Spend', { align: 'right' }),
    ];
    const headerRow = { rowId: 'header', height: GRID.size.headerRow, cells: headerCells };

    const dataRows = entities.map((e, idx) => ({
      rowId: e.entity_code,
      height: GRID.size.dataRow,
      cells: [
        cell.label(`${e.entity_code}${e.entity_name ? ' · ' + e.entity_name : ''}`, { idx }),
        ...(isCustomer ? [cell.text(e.group_name || '—', { idx })] : []),
        ...visibleCols.map((vc) =>
          vc.kind === 'group'
            ? cell.aggregate(r2(groupSpend(e.entity_code, vc.group)), { kind: 'quarter' })
            : cell.number(valueOf(e.entity_code, vc.type.code), { editable: true, idx })
        ),
        cell.number(r2(grossOf(e.entity_code)), { editable: false, idx }),
        cell.aggregate(r2(entityGiveaway(e.entity_code)), { kind: 'quarter' }),
        cell.aggregate(r2(entitySpend(e.entity_code)), { kind: 'year' }),
      ],
    }));

    // Totals — follow what's on screen.
    const totalGross = entities.reduce((a, e) => a + grossOf(e.entity_code), 0);
    const totalSpend = entities.reduce((a, e) => a + entitySpend(e.entity_code), 0);
    const totalCells = [
      cell.totalLabel('TOTAL'),
      ...(isCustomer ? [cell.totalBlank()] : []),
      ...visibleCols.map((vc) => {
        if (vc.kind === 'group') return cell.totalNumber(r2(entities.reduce((a, e) => a + groupSpend(e.entity_code, vc.group), 0)));
        // Summing a % across entities is meaningless, so only flat-amount type
        // columns get a column total; % columns stay blank.
        if (vc.type.basis === 'amt') return cell.totalNumber(r2(entities.reduce((a, e) => a + valueOf(e.entity_code, vc.type.code), 0)));
        return cell.totalBlank();
      }),
      cell.totalNumber(r2(totalGross)),
      cell.totalNumber(totalGross > 0 ? r2((totalSpend / totalGross) * 100) : 0),
      cell.totalNumber(r2(totalSpend)),
    ];
    const totalRow = { rowId: 'totals', height: GRID.size.totalRow, cells: totalCells };

    return [headerRow, ...dataRows, totalRow];
  }, [entities, visibleCols, isCustomer, groupSpend, valueOf, grossOf, entitySpend, entityGiveaway]);

  // ---- Interactions --------------------------------------------------------
  // Header clicks collapse/expand a group (same as Stocked Pricing stages).
  const handleFocus = (loc) => {
    if (loc.rowId !== 'header') return true;
    const col = String(loc.columnId);
    if (col.startsWith('G:')) { setCollapsed((p) => ({ ...p, [col.slice(2)]: false })); return false; }
    if (col.startsWith('T:')) {
      const code = col.slice(2);
      const group = effectiveGroups.find((g) => g.types.some((t) => t.code === code));
      if (group && group.types[0].code === code) { setCollapsed((p) => ({ ...p, [group.code]: true })); return false; }
    }
    return true;
  };

  const handleChanges = (changes) => {
    const touched = {};
    setValues((prev) => {
      const next = { ...prev };
      for (const ch of changes) {
        const col = String(ch.columnId);
        if (!col.startsWith('T:') || ch.rowId === 'header' || ch.rowId === 'totals') continue;
        const code = col.slice(2);
        const val = Number(ch.newCell?.value);
        if (isNaN(val)) continue;
        next[`${ch.rowId}:${code}`] = val;
        touched[`${ch.rowId}:${code}`] = true;
      }
      return next;
    });
    if (Object.keys(touched).length) setDirty((prev) => ({ ...prev, ...touched }));
  };

  // ---- Save ----------------------------------------------------------------
  const handleSave = async () => {
    setError(null);
    const rows = [];
    for (const e of entities) {
      for (const t of effectiveTypes) {
        const v = valueOf(e.entity_code, t.code);
        if (v !== 0 || dirty[`${e.entity_code}:${t.code}`]) {
          rows.push({ entity_code: e.entity_code, discount_code: t.code, value: v });
        }
      }
    }
    if (rows.length === 0 || selectedPeriods.length === 0) return;
    setIsSaving(true);
    try {
      const res = await discountService.upsertDiscounts({
        version_id: selectedVersion, level, financial_periods: selectedPeriods, rows,
      });
      if (!res.success) throw new Error(res.message);
      await loadData();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save discounts.');
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Toggles -------------------------------------------------------------
  const togglePeriod = (p) =>
    setSelectedPeriods((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p].sort((a, b) => a - b)));
  const toggleGroupHidden = (code) => setHiddenGroups((prev) => ({ ...prev, [code]: !prev[code] }));
  const collapseAll = () => setCollapsed({});
  const expandAll = () => setCollapsed(Object.fromEntries(effectiveGroups.map((g) => [g.code, false])));

  const hasChanges = Object.keys(dirty).length > 0;
  const noTypes = effectiveTypes.length === 0;

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-4">
        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Discounts &amp; Trade Spend</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          The give-away per {isCustomer ? 'customer' : 'customer group'} — % of gross or flat amounts, grouped into collapsible categories. Bridges gross revenue to net.
        </p>
        {error && <div className="mt-3 p-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">{error}</div>}

        <div className="mt-4 flex flex-wrap items-start gap-4">
          <Field label="Planning Level">
            <div className="flex items-center gap-1">
              {LEVELS.map((l) => (
                <button key={l.code} type="button" onClick={() => setLevel(l.code)} className={pillClass(level === l.code)}>{l.label}</button>
              ))}
            </div>
          </Field>

          <Field label="Months (discounts apply to)">
            <div className="flex flex-wrap items-center gap-1">
              {PERIODS.map((p) => (
                <button key={p} type="button" onClick={() => togglePeriod(p)} className={pillClass(selectedPeriods.includes(p))}>
                  {periodLabel(p, fyStartMonth)}
                </button>
              ))}
              <button type="button" onClick={() => setSelectedPeriods([...PERIODS])} className="ml-1 text-xs text-slate-500 dark:text-slate-400 hover:underline">All</button>
              <button type="button" onClick={() => setSelectedPeriods([])} className="text-xs text-slate-500 dark:text-slate-400 hover:underline">None</button>
            </div>
            {primaryPeriod && (
              <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400 max-w-md">
                Editing <span className="font-semibold text-slate-700 dark:text-slate-200">{periodLabel(primaryPeriod, fyStartMonth)}</span>.
                Saving writes to all <span className="font-semibold text-slate-700 dark:text-slate-200">{selectedPeriods.length}</span> selected month{selectedPeriods.length === 1 ? '' : 's'}.
              </p>
            )}
          </Field>

          {effectiveGroups.length > 0 && (
            <Field label="Groups (shown)">
              <div className="flex flex-wrap items-center gap-1">
                {effectiveGroups.map((g) => {
                  const shown = !hiddenGroups[g.code];
                  return (
                    <button key={g.code} type="button" onClick={() => toggleGroupHidden(g.code)}
                      title={shown ? `Hide ${g.label}` : `Show ${g.label}`}
                      className={`px-2 py-1 text-xs rounded border transition-colors ${
                        shown
                          ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200'
                          : 'bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500 border-slate-300 dark:border-slate-600 line-through'
                      }`}>
                      {g.short}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6">
        <div className="h-full rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {!selectedVersion ? (
            <div className="p-10 text-center text-sm text-slate-400">Select a budget version in the top bar to begin.</div>
          ) : noTypes ? (
            <div className="p-10 text-center text-sm text-slate-400">
              No discount types are enabled.{' '}
              <button type="button" onClick={() => navigate('/revenue/discount-config')} className="text-slate-600 dark:text-slate-300 underline">Configure discount types</button>.
            </div>
          ) : !primaryPeriod ? (
            <div className="p-10 text-center text-sm text-slate-400">Select at least one month to plan discounts for.</div>
          ) : (
            <MantleGrid
              rows={gridRows}
              columns={columns}
              isLoading={isLoading}
              onCellsChanged={handleChanges}
              onFocusLocationChanging={handleFocus}
              hint="Click a group header to expand it and edit each type; click the first type header to collapse it back. Collapsed groups show their R spend. Gross is derived from volumes × prices."
              toolbar={
                <>
                  <MantleGridButton onClick={() => navigate('/revenue/discount-config')}>
                    <span className="inline-flex items-center gap-1"><SlidersHorizontal className="w-3 h-3" /> Configure types</span>
                  </MantleGridButton>
                  <MantleGridButton onClick={expandAll}>Expand all</MantleGridButton>
                  <MantleGridButton onClick={collapseAll}>Collapse all</MantleGridButton>
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !hasChanges || selectedPeriods.length === 0}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white disabled:opacity-50 shadow-sm transition-colors ${
                      hasChanges ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600'
                    }`}
                  >
                    <Save className="w-3.5 h-3.5" />
                    {isSaving ? 'Saving…' : `Save to ${selectedPeriods.length} month${selectedPeriods.length === 1 ? '' : 's'}`}
                  </button>
                </>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Discounts;
